import { useEffect } from 'react';
import type { FC, ReactNode } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { captureException } from '@sentry/react';
import { Avatar, Col, Dropdown, Menu, Row, Tag } from 'antd';
import type { AxiosResponse } from 'axios';
import { TbArrowUpRight, TbCreditCard, TbDeviceDesktopDown, TbSettings } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';

import { authorizedAxiosInstance } from '@/clients/axios/authorized';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import useAccountSwitcherItems from '@/shared/components/dropdowns/AccountSwitcherItems';
import CountercyclicalDropdownFooter from '@/shared/components/dropdowns/CountercyclicalDropdownFooter';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useUserStore } from '@/stores/database/entities/00-RBAC/01-Users/store';
import { usePresenceStore } from '@/stores/realtime/presence';
import { shiftKey } from '@/utilities/constants/specialKeyboardCharacters';
import { getInternalPathFromLocation, sanitizeRedirect } from '@/utilities/redirects';

import { StatusBadge } from '../../badges';

interface UserDropdownProps {
    children: ReactNode;
    authenticated?: boolean;
}

const UserDropdown: FC<UserDropdownProps> = ({ children, authenticated }): JSX.Element => {
    const accountItems = useAccountSwitcherItems({ section: 'actions', logoutScope: 'current' });
    const { token } = useAntDesignTheme();

    const navigate = useNavigate();
    const location = useLocation();

    // !TODO: Refactor into a single-use function
    const handleGoToBillingPortal = async () => {
        try {
            toast('Opening Billing Portal...', {
                icon: <LoadingOutlined spin />,
            });

            await authorizedAxiosInstance
                .post(`/api/billing/billing-portal`, {
                    redirect: sanitizeRedirect(
                        getInternalPathFromLocation(location, { stripRedirect: true, stripStripeStatus: true }),
                    ),
                })
                .then((res: AxiosResponse) => {
                    if (res?.status === 200) {
                        window.open(`${res?.data?.url}`, '_blank');
                    }
                });
        } catch (error) {
            captureException(error);
        }
    };

    const { selectedUser: user, fetchUser } = useUserStore();
    const { planTier } = useEntitlements();

    useEffect(() => {
        if (!user?.id) {
            fetchUser();
        }
    }, [fetchUser, user?.id]);

    const { users } = usePresenceStore();

    const presence = users[user?.id as string];
    const status = presence?.status ?? 'offline';

    // const lastSeenAt = presence?.lastSeenAt;

    return (
        <Dropdown
            trigger={['click']}
            popupRender={() => (
                <DropdownMenuPanel
                    panelStyle={{ marginTop: '1.25rem', minWidth: '18rem' }}
                    style={{ paddingBottom: 0 }}
                >
                    <div style={{ textAlign: 'center', padding: '1rem 1rem 0.5em 1rem' }}>
                        <StatusBadge
                            borderColor={token.colorBgContainer}
                            borderWidth={2}
                            dotSize={14}
                            offset={[-10, 54]}
                            status={status}
                            style={{
                                zIndex: 100,
                            }}
                        >
                            <Avatar src={user?.avatar} style={{ marginBottom: '0.5rem' }} size={64} />
                        </StatusBadge>
                        <br />
                        <span>
                            <strong>
                                {user?.firstName} {user?.lastName}
                            </strong>
                        </span>
                        <br />
                        <small style={{ color: token.colorTextTertiary }}>{user?.emailAddress}</small>
                    </div>
                    {authenticated && (
                        <>
                            <Menu.Divider />
                            {/* <Menu.Item key="status" disabled>
                                        <Row>
                                            <Col span={18} style={{ textAlign: 'left' }}>
                                                <TbInnerShadowBottomRight
                                                    style={{ marginRight: '0.5rem', verticalAlign: '-0.2rem' }}
                                                    size={'1rem'}
                                                />
                                                Set to Away
                                            </Col>
                                            <Col span={6} style={{ textAlign: 'right' }}>
                                            </Col>
                                        </Row>
                                    </Menu.Item>
                                    <Menu.Divider /> */}
                            {/* <Menu.Item
                                key='profile'
                                onClick={() => {
                                    navigate(`/profile/overview`);
                                }}
                            >
                                <Row>
                                    <Col flex='auto' style={{ textAlign: 'left' }}>
                                        <TbUser
                                            style={{ marginRight: '0.5rem', verticalAlign: '-0.2rem' }}
                                            size={'1rem'}
                                        />
                                        Profile
                                    </Col>
                                    <Col flex='none' style={{ textAlign: 'right' }}>
                                        <Tag
                                            style={{
                                                marginRight: 0,
                                                paddingRight: 0,
                                                color: token.colorTextTertiary,
                                            }}
                                            color='transparent'
                                        >
                                            {shiftKey} U
                                        </Tag>
                                    </Col>
                                </Row>
                            </Menu.Item> */}
                            <Menu.Item
                                key='settings'
                                onClick={() => {
                                    navigate('/settings/account/general/overview');
                                }}
                            >
                                <Row>
                                    <Col flex='auto' style={{ textAlign: 'left' }}>
                                        <TbSettings
                                            style={{ marginRight: '0.5rem', verticalAlign: '-0.2rem' }}
                                            size={'1rem'}
                                        />
                                        Account Settings
                                    </Col>
                                    <Col flex='none' style={{ textAlign: 'right' }}>
                                        <Tag
                                            style={{
                                                marginRight: 0,
                                                paddingRight: 0,
                                                color: token.colorTextTertiary,
                                            }}
                                            color='transparent'
                                        >
                                            {shiftKey} X
                                        </Tag>
                                    </Col>
                                </Row>
                            </Menu.Item>
                            <Menu.Item
                                key='billing'
                                onClick={() => {
                                    navigate(`/settings/account/plans/billing`);
                                }}
                            >
                                <Row>
                                    <Col flex='auto' style={{ textAlign: 'left' }}>
                                        <TbCreditCard
                                            style={{ marginRight: '0.5rem', verticalAlign: '-0.2rem' }}
                                            size={'1rem'}
                                        />
                                        Plans & Billing
                                    </Col>
                                </Row>
                            </Menu.Item>
                        </>
                    )}
                    <Menu.Divider />
                    <Menu.Item
                        key='download'
                        onClick={() => {
                            window.open('https://countercyclical.com/download', '_blank');
                        }}
                    >
                        <Row>
                            <Col flex='auto' style={{ textAlign: 'left' }}>
                                <TbDeviceDesktopDown
                                    style={{ marginRight: '0.5rem', verticalAlign: '-0.2rem' }}
                                    size={'1rem'}
                                />
                                Download Desktop App
                                <TbArrowUpRight
                                    style={{
                                        verticalAlign: '-0.125rem',
                                        color: token.colorTextTertiary,
                                        marginLeft: '0.375rem',
                                    }}
                                />
                            </Col>
                        </Row>
                    </Menu.Item>
                    {planTier !== 'free' && (
                        <Menu.Item key='manageBillingPortal' onClick={handleGoToBillingPortal}>
                            <Row>
                                <Col flex='auto' style={{ textAlign: 'left' }}>
                                    <svg
                                        xmlns='http://www.w3.org/2000/svg'
                                        width='1rem'
                                        height='1rem'
                                        style={{ verticalAlign: '-0.2rem', marginRight: '0.5rem' }}
                                        viewBox='0 0 24 24'
                                        fill='none'
                                        stroke='currentColor'
                                        strokeWidth='2'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        className='icon icon-tabler icons-tabler-outline icon-tabler-credit-card-pay'
                                    >
                                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                        <path d='M12 19h-6a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v4.5' />
                                        <path d='M3 10h18' />
                                        <path d='M16 19h6' />
                                        <path d='M19 16l3 3l-3 3' />
                                        <path d='M7.005 15h.005' />
                                        <path d='M11 15h2' />
                                    </svg>
                                    Manage Billing
                                    <TbArrowUpRight
                                        style={{
                                            verticalAlign: '-0.125rem',
                                            color: token.colorTextTertiary,
                                            marginLeft: '0.375rem',
                                        }}
                                    />
                                </Col>
                            </Row>
                        </Menu.Item>
                    )}
                    <Menu.Divider />
                    {accountItems}
                    <CountercyclicalDropdownFooter />
                </DropdownMenuPanel>
            )}
            placement='bottomRight'
        >
            <span>{children}</span>
        </Dropdown>
    );
};

export default UserDropdown;
